import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { useCallback, useRef, useState, useEffect } from "preact/hooks";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { useSkinStore, resolveSpineAsset } from "../../../store/skinStore";
import { CharacterAnim } from "../PioneerGame/index.enums";
import { useSpineHelpers } from "./useSpineHelpers";

const CHARACTER_ANIMS = [
  CharacterAnim.IDLE,
  CharacterAnim.CLIMB,
  CharacterAnim.FALL,
] as const;
interface UseSpineCharacterProps {
  skinName?: string;
  onAnimationComplete?: (_animationName: string) => void;
  gameState?: GameState;
}

export function useSpineCharacter({
  skinName,
  onAnimationComplete,
  gameState,
}: UseSpineCharacterProps) {
  const { getSkin } = useSkinStore();
  const [currentAnim, setCurrentAnim] = useState<CharacterAnim | null>(null);
  const [skins, setSkins] = useState<string[] | null>(null);
  const [currentSkin, setCurrentSkin] = useState<string | null>(null);
  const spineRef = useRef<Spine | null>(null);
  const anims = CHARACTER_ANIMS;

  const { fitAndPosition } = useSpineHelpers();

  const createCharacter = useCallback(
    async (parent: PIXI.Container, w: number, h: number) => {
      try {
        const oldSpine = spineRef.current;

        const skin = getSkin("pioneer");
        if (!skin.pioneer) {
          throw new Error("Pioneer asset not found in skin");
        }
        const resolved = resolveSpineAsset(skin.pioneer);
        const skel = resolved.skeleton;
        const atl = resolved.atlas;
        await PIXI.Assets.load([skel, atl]);
        const spineBoy = Spine.from({
          skeleton: skel,
          atlas: atl,
          scale: 1,
          autoUpdate: true,
        });
        if (!spineBoy) throw new Error("Spine.from returned undefined");

        if (oldSpine) {
          oldSpine.destroy();
        }

        spineBoy.zIndex = 3;
        spineRef.current = spineBoy;
        parent.addChild(spineBoy);
        try {
          parent.setChildIndex(spineBoy, parent.children.length - 1);
        } catch {
          // ignore
        }

        fitAndPosition(spineBoy, w, h);

        const availableSkins = spineBoy.skeleton.data.skins.map((s) => s.name);
        setSkins(availableSkins);

        if (skinName && availableSkins.includes(skinName)) {
          spineBoy.skeleton.setSkinByName(skinName);
          spineBoy.skeleton.setSlotsToSetupPose();
          spineBoy.state.apply(spineBoy.skeleton);
          setCurrentSkin(skinName);
        } else {
          const fallbackSkin = availableSkins.includes("default")
            ? "default"
            : (availableSkins[0] ?? null);
          if (fallbackSkin) {
            spineBoy.skeleton.setSkinByName(fallbackSkin);
            spineBoy.skeleton.setSlotsToSetupPose();
            spineBoy.state.apply(spineBoy.skeleton);
            setCurrentSkin(fallbackSkin);
          }
        }

        const idleAnim = CharacterAnim.IDLE;
        spineBoy.state.setAnimation(0, idleAnim, true);
        setCurrentAnim(idleAnim);

        return spineBoy;
      } catch (error) {
        console.error("useSpineCharacter: ошибка создания персонажа:", error);
        throw error;
      }
    },
    [skinName, fitAndPosition, getSkin]
  );

  useEffect(() => {
    const spine = spineRef.current;
    if (!spine) return;

    const play = (name: CharacterAnim, loop: boolean) => {
      try {
        const entry = spine.state.setAnimation(0, name, loop);
        setCurrentAnim(name);
        return entry;
      } catch (e) {
        console.warn("useSpineCharacter: cannot play", name, e);
        return null;
      }
    };

    if (gameState === GameStateEnum.COUNTDOWN) {
      const idleAnim = CharacterAnim.IDLE;
      if (idleAnim && currentAnim !== idleAnim) {
        play(idleAnim, true);
      }
      return;
    }

    if (gameState === GameStateEnum.CLIMBING) {
      const climb = CharacterAnim.CLIMB;
      if (currentAnim !== climb) {
        play(climb, true);
      }
      return;
    }

    if (gameState === GameStateEnum.CRASHED) {
      const fallName = CharacterAnim.FALL;
      if (currentAnim !== fallName) {
        const entry = play(fallName, false);
        if (entry && onAnimationComplete && spine) {
          const listener = {
            complete: () => {
              onAnimationComplete(fallName);
              try {
                spine.state.removeListener(listener as any);
              } catch {}
            },
          };
          try {
            spine.state.addListener(listener as any);
          } catch {}
        }
      }
      return;
    }
  }, [gameState, anims, currentAnim, onAnimationComplete]);

  const changeAnimation = useCallback(
    (name: string) => {
      const spine = spineRef.current;
      if (!spine) return;
      if (!CHARACTER_ANIMS.includes(name as CharacterAnim)) return;
      const typedName = name as CharacterAnim;

      try {
        const isLooping =
          typedName === CharacterAnim.IDLE || typedName === CharacterAnim.CLIMB;
        const trackEntry = spine.state.setAnimation(0, typedName, isLooping);

        if (!isLooping) {
          trackEntry.listener = {
            complete: () => {
              if (onAnimationComplete) {
                onAnimationComplete(typedName);
              }
            },
          };
        }

        setCurrentAnim(typedName);
      } catch (error) {
        console.error("useSpineCharacter: ошибка при смене анимации:", error);
      }
    },
    [onAnimationComplete]
  );

  const resizeCharacter = useCallback(
    (w: number, h: number) => {
      const spine = spineRef.current;
      if (!spine) return;

      fitAndPosition(spine, w, h);
    },
    [fitAndPosition]
  );

  const checkCharacterHealth = useCallback(() => {
    const spine = spineRef.current;
    if (!spine) return false;

    try {
      if (!spine.skeleton || !spine.state) {
        console.warn("useSpineCharacter: Spine объект поврежден");
        return false;
      }

      if (spine.state && currentAnim) {
        const [currentTrack] = spine.state.tracks;
        if (!currentTrack || !currentTrack.animation) {
          console.warn("useSpineCharacter: анимация не воспроизводится");
          return false;
        }
      }

      return true;
    } catch (error) {
      console.warn("useSpineCharacter: ошибка при проверке состояния:", error);
      return false;
    }
  }, [currentAnim]);

  const destroyCharacter = useCallback((app?: PIXI.Application) => {
    const spine = spineRef.current;
    if (spine) {
      if (app) {
        app.stage.removeChild(spine);
      }
      spine.destroy();
      spineRef.current = null;
      setCurrentAnim(null);
      setSkins(null);
      setCurrentSkin(null);
    }
  }, []);

  return {
    spineRef,
    anims,
    currentAnim,
    skins,
    currentSkin,
    createCharacter,
    changeAnimation,
    resizeCharacter,
    checkCharacterHealth,
    destroyCharacter,
    fitAndPosition,
  };
}
